<template>
  <!-- 销售商机分析：报表类，按商机阶段过滤 -->
  <report-layout active="opportunity">
    <div class="opportunity-report">
    <list-page>
      <search-condition @search="loadReport" :show-search="true" page-name="opportunityReport">
        <template #default>
          <yq-date-range v-model:start-time="query.startTime" v-model:end-time="query.endTime" class="double-width">
            <template #prefix>创建日期</template>
          </yq-date-range>
          <el-select v-model="query.currentStage" placeholder="全部" clearable>
            <template #prefix>商机阶段</template>
            <el-option v-for="s in stageList" :key="s.userCode" :label="s.dictName" :value="Number(s.userCode)"/>
          </el-select>
          <employee-select v-model="query.ownerEmployeeId" placeholder="全部" clearable>
            <template #prefix>负责人</template>
          </employee-select>
        </template>
        <template #option>
          <page-button @click="resetAll">重置</page-button>
          <page-button type="read" el-type="primary" @click="loadReport">查询</page-button>
        </template>
      </search-condition>

      <list-table background-color="#FFF">
        <div v-loading="loading" class="report-body">
          <!-- KPI -->
          <div class="stat-grid">
            <div class="stat-card">
              <div class="label">商机总数</div>
              <div class="value">{{ report.opportunityCount || 0 }}</div>
            </div>
            <div class="stat-card">
              <div class="label">商机总金额</div>
              <div class="value money">{{ money(report.opportunityAmount) }}</div>
            </div>
            <div class="stat-card">
              <div class="label">加权预测金额</div>
              <div class="value money">{{ money(report.predictAmount) }}</div>
            </div>
          </div>

          <!-- 销售漏斗分析 -->
          <div class="chart-card">
            <div class="section-title">销售漏斗分析</div>
            <div class="funnel-wrap">
              <div ref="funnelChart" class="chart-div"></div>
              <div class="funnel-legend">
                <div v-for="(s, i) in funnelRows" :key="s.stageValue" class="fl-row">
                  <span class="fl-name">{{ s.stageTitle }}</span>
                  <span class="fl-track">
                    <i :style="{width: barWidth(s) + '%', background: funnelColor(i)}"></i>
                  </span>
                  <span class="fl-val">{{ s.opportunityCount || 0 }}</span>
                  <span class="fl-rate">{{ s.convertRate === null || s.convertRate === undefined ? '—' : s.convertRate + '%' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 两张阶段柱图 -->
          <div class="grid-2">
            <div class="chart-card">
              <div class="section-title">阶段商机数量</div>
              <div ref="countChart" class="chart-div"></div>
            </div>
            <div class="chart-card">
              <div class="section-title">阶段商机金额（元）</div>
              <div ref="amountChart" class="chart-div"></div>
            </div>
          </div>

          <!-- 统计明细列表 -->
          <div class="chart-card">
            <div class="section-title">统计明细列表</div>
            <el-table :data="chartStageRows" border size="small" :empty-text="'暂无数据'">
              <el-table-column label="序号" width="70" align="center">
                <template v-slot="{$index}">{{ $index + 1 }}</template>
              </el-table-column>
              <el-table-column label="商机阶段" prop="stageTitle">
                <template v-slot="{row}">
                  <el-tag size="small" :type="stageTagType(row.stageValue)">{{ row.stageTitle }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="商机数量" prop="opportunityCount" align="right">
                <template v-slot="{row}">{{ row.opportunityCount || 0 }}</template>
              </el-table-column>
              <el-table-column label="商机金额" prop="opportunityAmount" align="right">
                <template v-slot="{row}">{{ money(row.opportunityAmount) }}</template>
              </el-table-column>
              <el-table-column label="金额占比" prop="amountRatio" align="right">
                <template v-slot="{row}">{{ row.amountRatio || 0 }}%</template>
              </el-table-column>
              <el-table-column label="数量占比" prop="countRatio" align="right">
                <template v-slot="{row}">{{ row.countRatio || 0 }}%</template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </list-table>
    </list-page>
    </div>
  </report-layout>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ReportLayout from "@/views/h5/crm/report/ReportLayout.vue";
import YqDateRange from "@/components/h5/date/YqDateRange.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import opportunityReportApi from "@/api/dz-crm/partner/opportunityReportApi";
import {DICT_OPPORTUNITY_STAGE, fillStageRows, stageTagType} from "@/views/h5/crm/crmDictionary";
import commonApi from "@/api/commonApi";
import {useRoute, useRouter} from "vue-router";

const FUNNEL_COLORS = ["#69b1ff", "#4096ff", "#1677ff", "#0958d9", "#003eb3", "#002c8c"];

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "OpportunityReport",
  components: {ReportLayout, YqDateRange, EmployeeSelect, ListPage, ListTable, SearchCondition, PageButton},
  data() {
    return {
      loading: false,
      query: {startTime: "", endTime: "", currentStage: "", ownerEmployeeId: ""},
      report: {},
      stageList: [],
      dictReady: false,
      dataReady: false,
      funnelChart: null,
      countChart: null,
      amountChart: null,
    };
  },
  beforeRouteLeave(to, from, next) {
    // 报表页含 echarts 实例，$data 不能 JSON 序列化（循环引用），不做页面缓存
    next();
  },
  mounted() {
    commonApi.queryUniqueDictionaryList(DICT_OPPORTUNITY_STAGE).then(list => {
      this.stageList = list || [];
      this.dictReady = true;
      this.renderIfReady();
    });
    this.loadReport();
  },
  computed: {
    /**
     * 阶段统计：以唯一性字典为准取全部阶段，没有数据的阶段补 0，
     * 与概况页口径一致 —— 统计明细列表与两张柱图都用它。
     */
    chartStageRows() {
      return fillStageRows(this.stageList, this.report.stageStats);
    },
    /** 漏斗层：剔除关闭阶段（已流失），同样按字典补 0 */
    funnelRows() {
      return fillStageRows(this.stageList, this.report.stageStats, {excludeClose: true});
    },
  },
  unmounted() {
    [this.funnelChart, this.countChart, this.amountChart].forEach(chart => chart && chart.dispose());
  },
  methods: {
    resetAll() {
      this.query = {startTime: "", endTime: "", currentStage: "", ownerEmployeeId: ""};
      this.loadReport();
    },
    async loadReport() {
      this.loading = true;
      try {
        const params = {};
        Object.keys(this.query).forEach(k => {
          let v = this.query[k];
          if (v === "" || v === null || v === undefined) {
            return;
          }
          if (k === "startTime" || k === "endTime") {
            v = String(v).slice(0, 10);
          }
          params[k] = v;
        });
        this.report = await opportunityReportApi.report(params) || {};
        this.dataReady = true;
        this.$nextTick(() => this.renderIfReady());
      } finally {
        this.loading = false;
      }
    },
    /** 字典与统计数据都到位才渲染图表 */
    renderIfReady() {
      if (this.dictReady && this.dataReady) {
        this.renderCharts();
      }
    },
    renderCharts() {
      // 阶段名称与补 0 由 chartStageRows 统一处理
      const rows = this.chartStageRows;
      const names = rows.map(s => s.stageTitle);
      this.funnelChart = this.renderFunnel(this.funnelChart, names);
      this.countChart = this.renderBar(this.countChart, this.$refs.countChart, names,
          rows.map(s => s.opportunityCount), "#1677ff");
      this.amountChart = this.renderBar(this.amountChart, this.$refs.amountChart, names,
          rows.map(s => s.opportunityAmount), "#13b8b8");
    },
    renderFunnel(chart, names) {
      const dom = this.$refs.funnelChart;
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c}"},
        color: FUNNEL_COLORS,
        series: [{
          type: "funnel",
          left: "10%",
          right: "10%",
          top: 10,
          bottom: 10,
          minSize: "20%",
          label: {show: true, position: "inside", formatter: "{b}"},
          data: this.funnelRows.map(s => ({name: s.stageTitle, value: s.opportunityCount || 0})),
        }],
      });
      return chart;
    },
    renderBar(chart, dom, xNames, data, color) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}},
        grid: {left: "0", right: "0", bottom: "3%", top: "10%", containLabel: true},
        xAxis: {type: "category", data: xNames, axisTick: {alignWithLabel: true}, axisLabel: {color: "#909399"}},
        yAxis: {type: "value", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        series: [{type: "bar", barWidth: "40%", itemStyle: {color: color}, data: data}],
      });
      return chart;
    },
    barWidth(stat) {
      const first = this.funnelRows.length ? (this.funnelRows[0].opportunityCount || 0) : 0;
      if (!first) {
        return 0;
      }
      return Math.min(100, (stat.opportunityCount || 0) / first * 100).toFixed(1);
    },
    funnelColor(index) {
      return FUNNEL_COLORS[index % FUNNEL_COLORS.length];
    },
    stageTagType(stageValue) {
      return stageTagType(stageValue);
    },
    money(value) {
      if (value === null || value === undefined) {
        return "0.00";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
  },
};
</script>

<style lang="scss" scoped>
.opportunity-report {
  height: 100%;
}

.report-body {
  padding: 4px;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money {
        font-size: 20px;
      }
    }
  }
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
  }

  .chart-div {
    height: 260px;
    width: 100%;
  }
}

.funnel-wrap {
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;

  .chart-div {
    flex: 0 0 320px;
    height: 270px;
  }

  .funnel-legend {
    flex: 1;
    min-width: 280px;

    .fl-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 0;
      font-size: 13px;

      .fl-name {
        width: 80px;
        color: #303133;
      }

      .fl-track {
        flex: 1;
        height: 10px;
        background: #f0f2f5;
        border-radius: 5px;
        overflow: hidden;

        i {
          display: block;
          height: 100%;
          border-radius: 5px;
        }
      }

      .fl-val {
        width: 60px;
        text-align: right;
        font-weight: 600;
        color: #303133;
      }

      .fl-rate {
        width: 60px;
        text-align: right;
        color: #909399;
      }
    }
  }
}
</style>
